import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import {
  useGetDeployedEntitySnapshot,
  useSearchDeployedChildEntities,
} from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import _filter from 'lodash/filter';
import _toNumber from 'lodash/toNumber';
import { useEnvVariables } from '../../../../../../hooks/useEnvVariables';
import { useFetchInterfacePermissions } from '../../../../../access-control/hooks/useFetchInterfacePermissions';
import { useDeployedInterfaceDetails } from '../../../../../context/PublishedInterfaceProvider';
import InterfaceHelper from '../../../../../stores/InterfaceStore/helper/InterfaceHelper';
import { getLookupInterfaceQueryFilter } from '../../../../../utils/interfaceQueryFilters';
import type { UseFetchInterfaceAppResourcesHookType } from '../../../types';
import { useFetchDeployedDataSourcesByComponentIdAndVersion } from '../useFetchDeployedDataSourcesByComponentIdAndVersion';

const useFetchDeployedInterfaceAppResourcesByVersion: UseFetchInterfaceAppResourcesHookType = ({
  interfaceId,
  mode,
  client,
}) => {
  const { deployedVersion } = useDeployedInterfaceDetails();
  const {
    data: interfaceRecord,
    isLoading: isLoadingInterfaceRecord,
    error: errorInterfaceRecord,
  } = useGetDeployedEntitySnapshot<InterfaceEntity | undefined>(
    UAEntityTypes.Interface,
    interfaceId,
    {
      version: deployedVersion,
    },
  );

  const globalInterfacePageComponent = InterfaceHelper.getGlobalInterfacePageComponent(interfaceId);

  const {
    data: globalInterfacePageEntities,
    isLoading: isLoadingGlobalInterfacePage,
    error: errorGlobalInterfacePage,
  } = useSearchDeployedChildEntities<InterfacePageEntity[]>({
    entityType: UAEntityTypes.Component,
    parentEntityId: interfaceId,
    parentEntityType: UAEntityTypes.Interface,
    parentVersion: _toNumber(deployedVersion),
    filter: getLookupInterfaceQueryFilter({
      interfaceId,
      interfaceComponent: globalInterfacePageComponent,
    }).filter,
  });

  const globalInterfacePage = _filter(globalInterfacePageEntities, {
    id: globalInterfacePageComponent.id,
  })[0] as InterfacePageEntity | undefined;

  const { dataSources, errorDS, isLoadingDS } = useFetchDeployedDataSourcesByComponentIdAndVersion({
    interfaceComponentId: globalInterfacePageComponent.id,
    interfaceComponentVersion: globalInterfacePage?.version
      ? _toNumber(globalInterfacePage.version)
      : undefined,
  });

  const {
    permissions,
    isLoading: permissionsLoading,
    error: permissionsError,
  } = useFetchInterfacePermissions({
    interfaceId,
    client,
    mode,
  });

  const {
    envVariables,
    isLoading: envVariablesLoading,
    error: envVariablesError,
  } = useEnvVariables();

  const _isLoading =
    isLoadingInterfaceRecord ||
    isLoadingGlobalInterfacePage ||
    permissionsLoading ||
    envVariablesLoading ||
    isLoadingDS;

  const _error =
    errorInterfaceRecord ||
    errorGlobalInterfacePage ||
    permissionsError ||
    envVariablesError ||
    errorDS;

  return {
    isLoading: _isLoading,
    error: _error,
    interfaceRecord,
    permissions: permissions ?? {},
    envVariables,
    dataSources,
    globalInterfacePage,
  };
};

export default useFetchDeployedInterfaceAppResourcesByVersion;
